import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { Trash2 } from 'lucide-react'

import FbButton from 'components/FbUI/FbButton'
import {
  type SenderEmail,
  useDetachCampaignSenderEmail,
  useGetCampaignSequence,
  useGetCampaignSenderEmails,
  useSyncWorkspaceSenderEmails,
} from '../sequences_api'
import { AddSenderEmailsModal } from './AddSenderEmailsModal'
import { SenderEmailsEmptyState } from './SenderEmailsEmptyState'
import { SenderEmailsTable } from './SenderEmailsTable'
import * as S from 'components/Tables/CommonTable.styles'

interface SenderEmailsTabProps {
  campaignId: number
}

export function SenderEmailsTab({ campaignId }: SenderEmailsTabProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)

  // Get sequence to access bison_campaign_id
  const { data: sequence, isLoading: isLoadingSequence } =
    useGetCampaignSequence(campaignId)

  const bisonCampaignId = sequence?.bison_campaign_id

  const {
    data: senderEmails,
    isLoading: isLoadingSenderEmails,
    isFetching,
  } = useGetCampaignSenderEmails(bisonCampaignId || 0)

  const syncMutation = useSyncWorkspaceSenderEmails()
  const detachMutation = useDetachCampaignSenderEmail(bisonCampaignId || 0)

  const handleSync = async () => {
    try {
      const result = await syncMutation.mutateAsync()
      toast.success(
        `Synced: ${result.created} added, ${result.updated} updated`
      )
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || 'Failed to sync sender emails'
      toast.error(errorMessage)
    }
  }

  const handleRemove = useCallback(
    async (senderEmail: SenderEmail) => {
      try {
        await detachMutation.mutateAsync(senderEmail.id)
        toast.success('Sender email removed from campaign')
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail || 'Failed to remove sender email'
        toast.error(errorMessage)
      }
    },
    [detachMutation]
  )

  const getRowActions = useCallback(
    (senderEmail: SenderEmail) => [
      {
        label: 'Remove',
        icon: Trash2,
        onClick: () => handleRemove(senderEmail),
        variant: 'destructive' as const,
        disabled: detachMutation.isPending,
      },
    ],
    [handleRemove, detachMutation.isPending]
  )

  if (isLoadingSequence || (isLoadingSenderEmails && !senderEmails)) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (!bisonCampaignId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">No EmailBison campaign found</div>
      </div>
    )
  }

  if (senderEmails && senderEmails.results.length === 0) {
    return (
      <>
        <SenderEmailsEmptyState
          onSync={handleSync}
          onAdd={() => setIsAddModalOpen(true)}
          isSyncing={syncMutation.isPending}
        />
        <AddSenderEmailsModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          bisonCampaignId={bisonCampaignId}
        />
      </>
    )
  }

  const tableHeader = (
    <S.SearchContainer>
      <S.SearchContainerFilters>
        <div />
        <S.TableButtonsContainer>
          <FbButton
            variant="secondary"
            size="sm"
            onClick={handleSync}
            disabled={syncMutation.isPending}
            loading={syncMutation.isPending}
          >
            Sync Workspace
          </FbButton>
          <FbButton
            variant="primary"
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
          >
            Add Sender Emails
          </FbButton>
        </S.TableButtonsContainer>
      </S.SearchContainerFilters>
    </S.SearchContainer>
  )

  return (
    <>
      <SenderEmailsTable
        data={senderEmails?.results ?? []}
        isLoading={isFetching}
        tableHeader={tableHeader}
        getRowActions={getRowActions}
      />
      <AddSenderEmailsModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        bisonCampaignId={bisonCampaignId}
      />
    </>
  )
}
